import React, { ReactNode } from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { NavLink } from 'react-router-dom';
import {
  Box,
  Button,
  Circle,
  Flex,
  Icon,
  MenuButton,
  Tooltip,
} from '@chakra-ui/react';
import { CollapsedOptions } from '../CollapsedOptions/CollapsedOptions';
import { dropContent, optionProps } from '../NavButtons';
import { NavText } from '../NavText';

interface NavButtonProps {
  handlePick?: () => void;
  isActive: boolean;
  activeColor: string;
  name: string;
  path: string;
  option: optionProps;
  icon?: ReactNode;
  isCollapsed: boolean;
  isDroppable?: boolean;
  level: number;
  dropOptions?: dropContent[];
  onClose: () => void;
}

const ICON_W = '24px';

export const NavButton: React.FC<NavButtonProps> = ({
  activeColor,
  isActive,
  handlePick,
  icon,
  isCollapsed,
  isDroppable,
  name,
  option,
  path,
  level,
  dropOptions,
  onClose,
}) => {
  const handleClick = () => {
    if (handlePick) handlePick();
    onClose();
  };

  if (isCollapsed && isDroppable)
    return (
      <>
        <Tooltip label={name} bg="ocean.100" mt="-25px">
          <MenuButton>
            <Button
              as={Button}
              onClick={handlePick}
              minW={0}
              minH={0}
              py="26px"
              w="100%"
              _active={{ bg: activeColor, color: 'white', textColor: 'white' }}
              justifyContent="start"
              fontWeight={400}
              my="8px"
              isActive={isActive}
            >
              {level === 0 ? (
                <Flex>{icon}</Flex>
              ) : level === 1 ? (
                <Circle
                  bg="white"
                  opacity={isActive ? '100%' : '50%'}
                  size="10px"
                  ml="12px"
                  mr="20px"
                />
              ) : (
                <Flex py="10px" px="16px" borderRadius="8px" align="center">
                  <Circle
                    bg="white"
                    opacity={isActive ? '100%' : '50%'}
                    size="8px"
                    ml="24px"
                    mr="8px"
                  />
                </Flex>
              )}
            </Button>
          </MenuButton>
        </Tooltip>

        <CollapsedOptions dropOptions={dropOptions} />
      </>
    );

  if (isCollapsed && !isDroppable)
    return (
      <Tooltip label={name} bg="ocean.100">
        <NavLink to={path}>
          <Button
            as={Button}
            onClick={handleClick}
            minW={0}
            minH={0}
            py="26px"
            w="100%"
            _active={{ bg: activeColor, color: 'white', textColor: 'white' }}
            justifyContent="start"
            alignItems="center"
            fontWeight={400}
            my="8px"
            isActive={isActive}
          >
            {level === 0 ? (
              <Flex>{icon}</Flex>
            ) : level === 1 ? (
              <Circle
                bg="white"
                opacity={isActive ? '100%' : '50%'}
                size="10px"
                ml="12px"
                mr="20px"
              />
            ) : (
              <Flex py="10px" px="16px" borderRadius="8px" align="center">
                <Circle
                  bg="white"
                  opacity={isActive ? '100%' : '50%'}
                  size="8px"
                  ml="24px"
                  mr="8px"
                />
              </Flex>
            )}
          </Button>
        </NavLink>
      </Tooltip>
    );

  if (!isDroppable)
    return (
      <NavLink to={path}>
        <Button
          onClick={handleClick}
          minW={0}
          minH={0}
          py="26px"
          w="100%"
          _active={{ bg: activeColor, color: 'white', textColor: 'white' }}
          justifyContent="start"
          fontWeight={400}
          my="8px"
          isActive={isActive}
        >
          {level === 0 ? (
            isCollapsed ? (
              <Flex>{icon}</Flex>
            ) : (
              <Box pt="4px" pr="16px">
                {icon}
              </Box>
            )
          ) : level === 1 ? (
            <Circle
              bg="white"
              opacity={isActive ? '100%' : '50%'}
              size="10px"
              ml="12px"
              mr="20px"
            />
          ) : (
            <Flex py="10px" borderRadius="8px" align="center">
              <Circle
                bg="white"
                opacity={isActive ? '100%' : '50%'}
                size="8px"
                ml="24px"
                mr="20px"
              />
            </Flex>
          )}

          {!isCollapsed && <NavText name={name} isActive={isActive} />}
        </Button>
      </NavLink>
    );

  return (
    <Button
      onClick={handlePick}
      minW={0}
      minH={0}
      py="26px"
      w="100%"
      _active={{ bg: activeColor, color: 'white', textColor: 'white' }}
      justifyContent="start"
      fontWeight={400}
      my="8px"
      isActive={isActive}
    >
      {level === 0 ? (
        <Box pt="4px" pr="16px">
          {icon}
        </Box>
      ) : level === 1 ? (
        <Circle
          bg="white"
          opacity={isActive ? '100%' : '50%'}
          size="10px"
          ml="12px"
          mr="20px"
        />
      ) : (
        <Flex py="10px" px="16px" borderRadius="8px" align="center">
          <Circle
            bg="white"
            opacity={isActive ? '100%' : '50%'}
            size="8px"
            ml="24px"
            mr="8px"
          />
        </Flex>
      )}

      <NavText name={name} isActive={isActive} />

      <Icon
        as={IoIosArrowDown}
        h="14px"
        w={ICON_W}
        color="white"
        alignSelf="center"
        ml="auto"
        transform={
          level === 0
            ? option.fp.includes(name)
              ? 'rotate(-180deg)'
              : 'rotate(-360deg)'
            : option.sp.includes(name)
            ? 'rotate(-180deg)'
            : 'rotate(-360deg)'
        }
        transition="all .5s ease"
      />
    </Button>
  );
};
